import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "@/lib/react-router-dom-shim";
import { Breadcrumb } from "@/components/Breadcrumb";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { IterpecHotel, IterpecHotelRoom } from "@/types/iterpec";
import { HotelDetailLoading } from "@/components/HotelDetailLoading";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EnhancedImageGallery } from "@/components/EnhancedImageGallery";
import { LazyInteractiveHotelMap } from "@/components/LazyInteractiveHotelMap";
import { HotelStaticMap } from "@/components/HotelStaticMap";
import { HotelAmenities } from "@/components/HotelAmenities";
import { HotelManualInfoSection } from "@/components/HotelManualInfoSection";
import { HotelFAQ } from "@/components/HotelFAQ";
import { HotelManualInfoEditor } from "@/components/HotelManualInfoEditor";
import { HotelFAQEditor } from "@/components/HotelFAQEditor";
import { useHotelManualFaq } from "@/hooks/useHotelManualFaq";
import { RoomCard } from "@/components/RoomCard";
import { SearchEditModal } from "@/components/SearchEditModal";
import { NoAvailabilityModal } from "@/components/NoAvailabilityModal";
import { Star, MapPin, Calendar, Users, ArrowLeft, Wifi, Car, Coffee, Waves, CheckCircle, ChevronDown, ChevronUp, Filter, X, Building2, Bed, UtensilsCrossed, Dumbbell, Sparkles, TreePine, Mountain, Plane, Clock, Heart, Shield, Baby, Accessibility, ParkingCircle, Palmtree, Umbrella, Landmark, Info, Pencil, HelpCircle } from "lucide-react";
import { cleanHtmlText } from "@/utils/textUtils";
import { extractAmenitiesFromText } from "@/utils/hotelAmenities";
import { useTranslation as useHotelTranslation } from "@/hooks/useTranslation";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { format, parse, addDays } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { useCart } from "@/hooks/useCart";
import { useHotelManualInfo } from "@/hooks/useHotelManualInfo";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { SchemaOrg, getHotelDetailSchema, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getHotelProductSchema, getFAQSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";

const hotelProductSchema = z.object({
  hotelId: z.number().positive("ID do hotel é obrigatório"),
  roomId: z.string().min(1, "ID do quarto é obrigatório"),
  roomDescription: z.string().min(1, "Descrição do quarto é obrigatória").max(500, "Descrição muito longa"),
  bedDescription: z.string().max(200, "Descrição da cama muito longa").optional().nullable(),
  boardDescription: z.string().max(200, "Descrição da alimentação muito longa").optional().nullable(),
  priceEur: z.number().positive("Preço deve ser maior que zero"),
  hotelName: z.string().min(1, "Nome do hotel é obrigatório").max(300, "Nome do hotel muito longo"),
});

const HotelDetail = () => {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addItem } = useCart();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const { format: formatPrice, currency } = usePrice();
  const { locale } = useLocale();
  
  const dateLocale = i18n.language === 'pt-BR' ? ptBR : i18n.language === 'en' ? enUS : es;
  
  const [hotel, setHotel] = useState<IterpecHotel | null>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [visibleRoomsCount, setVisibleRoomsCount] = useState(5);
  const [isBooking, setIsBooking] = useState(false);
  const [boardTypeFilter, setBoardTypeFilter] = useState<string>('all');
  const [cancellationFilter, setCancellationFilter] = useState<string>('all');
  const [isSearchEditModalOpen, setIsSearchEditModalOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isNoAvailabilityModalOpen, setIsNoAvailabilityModalOpen] = useState(false);
  const [pendingSearchParams, setPendingSearchParams] = useState<{
    checkIn: string;
    numNights: number;
    adults: number;
    childrenAges: number[];
  } | null>(null);
  const hotelNameRef = useRef<HTMLHeadingElement>(null);
  const roomsSectionRef = useRef<HTMLDivElement>(null);
  
  // Fetch manual info for this hotel
  const { manualInfo, refetch: refetchManualInfo } = useHotelManualInfo(hotel?.HotelId);
  const { isAdmin } = useIsAdmin();
  const [isManualInfoEditorOpen, setIsManualInfoEditorOpen] = useState(false);
  const [isFaqEditorOpen, setIsFaqEditorOpen] = useState(false);
  const { refetch: refetchManualFaq } = useHotelManualFaq(hotel?.HotelId);
  const [hotelFaqItems, setHotelFaqItems] = useState<Array<{ question: string; answer: string }>>([]);
  const cleanedDescription = hotel ? cleanHtmlText(hotel.HotelDescription) : '';
  const targetLang = i18n.language.startsWith('pt') ? 'pt-BR' : i18n.language.startsWith('en') ? 'en' : 'es';
  // Translate content for non-Portuguese users, using auto-detection for source language
  const needsTranslation = !i18n.language.startsWith('pt');
  const { translatedText: translatedDescription, isTranslating } = useHotelTranslation(
    needsTranslation ? cleanedDescription : undefined,
    'auto',
    targetLang
  );
  const displayDescription = needsTranslation ? (translatedDescription || cleanedDescription) : cleanedDescription;
  
  const destinationId = searchParams.get('destinationId');
  const destinationName = searchParams.get('destinationName');
  const checkIn = searchParams.get('checkIn');
  const numNights = searchParams.get('numNights');
  const adults = searchParams.get('adults');
  const childrenAges = searchParams.get('childrenAges');
  const origin = searchParams.get('origin'); // 'resorts' if coming from resorts page
  const resortDestinationId = searchParams.get('resortDestinationId');

  useEffect(() => {
    if (!hotelId || !destinationId || !checkIn || !numNights || !adults) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: "destructive",
      });
      navigate('/hoteles');
      return;
    }
    fetchHotelDetails();
  }, [hotelId, destinationId, checkIn, numNights, adults, childrenAges]);

  useEffect(() => {
    if (hotel && hotelNameRef.current) {
      const yOffset = -120;
      const element = hotelNameRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [hotel]);

  const fetchHotelDetails = async () => {
    setIsLoading(true);
    try {
      const childAges = childrenAges ? childrenAges.split(',').map(Number) : [];
      
      const { data, error } = await supabase.functions.invoke('iterpec-hotel-details', {
        body: {
          destinationId,
          checkIn,
          numNights: parseInt(numNights!),
          adults: parseInt(adults!),
          childrenAges: childAges,
          hotelId: parseInt(hotelId!),
        },
      });

      if (error) throw error;

      if (data?.Hotels && data.Hotels.length > 0) {
        setHotel(data.Hotels[0]);
        const token = data.Token || data.SearchId || null;
        setSearchId(token);
        
        // Save static page in background for SEO
        const hotelData = data.Hotels[0];
        supabase.functions.invoke('save-hotel-static', {
          body: {
            hotel: hotelData,
            destinationId: destinationId,
            destinationName: destinationName || undefined,
            descriptions: {
              es: hotelData.HotelDescription || hotelData.Description,
              en: null,
              pt: hotelData.HotelDescription || hotelData.Description,
            }
          }
        }).then(result => {
          if (result.data?.success) {
            console.log('Static page saved:', result.data.slug);
          }
        }).catch(err => console.error('Error saving static page:', err));
      } else {
        toast({
          title: t('common.error'),
          description: t('hotels.noRoomsAvailable'),
          variant: "destructive",
        });
        navigate('/hoteles');
      }
    } catch (error) {
      console.error('Error fetching hotel details:', error);
      toast({
        title: t('common.error'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchWithNewParams = async (params: {
    checkIn: string;
    numNights: number;
    adults: number;
    childrenAges: number[];
  }) => {
    setIsSearching(true);
    setPendingSearchParams(params);

    try {
      const { data, error } = await supabase.functions.invoke('iterpec-hotel-details', {
        body: {
          destinationId,
          checkIn: params.checkIn,
          numNights: params.numNights,
          adults: params.adults,
          childrenAges: params.childrenAges,
          hotelId: parseInt(hotelId!),
        },
      });

      if (error) throw error;

      // Check if hotel has availability
      if (data?.TotalHotelResults === 0 || !data?.Hotels || data.Hotels.length === 0) {
        // No availability - show modal
        setIsSearchEditModalOpen(false);
        setIsNoAvailabilityModalOpen(true);
      } else {
        // Has availability - reload page with new params
        setIsSearchEditModalOpen(false);
        const childrenAgesParam = params.childrenAges.length > 0 
          ? `&childrenAges=${params.childrenAges.join(',')}` 
          : '';
        navigate(`/hoteles/${hotelId}?destinationId=${destinationId}&checkIn=${params.checkIn}&numNights=${params.numNights}&adults=${params.adults}${childrenAgesParam}`, { replace: true });
      }
    } catch (error) {
      console.error('Error searching hotel:', error);
      toast({
        title: t('common.error'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleStayOnPage = () => {
    setIsNoAvailabilityModalOpen(false);
    setPendingSearchParams(null);
  };

  const handleGoToResults = () => {
    if (pendingSearchParams) {
      const childrenAgesParam = pendingSearchParams.childrenAges.length > 0 
        ? `&childrenAges=${pendingSearchParams.childrenAges.join(',')}` 
        : '';
      navigate(`/hoteles?destinationId=${destinationId}&checkIn=${pendingSearchParams.checkIn}&numNights=${pendingSearchParams.numNights}&adults=${pendingSearchParams.adults}${childrenAgesParam}`);
    }
    setIsNoAvailabilityModalOpen(false);
    setPendingSearchParams(null);
  };

  const getRoomPriceEur = (room: IterpecHotelRoom) => room.PriceEur || 0;

  const getRoomPricePerNight = (room: IterpecHotelRoom) => {
    const totalPrice = getRoomPriceEur(room);
    const nights = parseInt(numNights || '1');
    return nights > 0 ? totalPrice / nights : totalPrice;
  };

  const formatCancellationPolicy = (room: IterpecHotelRoom) => {
    if (!room.CancellationPolicies || room.CancellationPolicies.length === 0) {
      return t('hotelDetail.nonRefundable');
    }
    
    const firstPolicy = room.CancellationPolicies[0];
    const parseDateOnly = (input: string) => {
      const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d));
      }
      const d = new Date(input);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const policyStartDate = parseDateOnly(firstPolicy.StartDate);
    const searchDate = new Date();
    searchDate.setHours(0, 0, 0, 0);
    const searchDatePlus5 = new Date(searchDate);
    searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);
    
    const checkInDateParsed = checkIn ? parse(checkIn, 'yyyy-MM-dd', new Date()) : new Date();
    const oneDayBeforeCheckIn = new Date(checkInDateParsed);
    oneDayBeforeCheckIn.setDate(oneDayBeforeCheckIn.getDate() - 1);

    if (policyStartDate <= searchDatePlus5) {
      return t('hotelDetail.nonRefundable');
    }
    
    if (policyStartDate >= oneDayBeforeCheckIn) {
      return t('hotelDetail.refundable');
    }
    
    const cancellationDate = new Date(policyStartDate);
    cancellationDate.setDate(cancellationDate.getDate() - 3);
    return t('hotelDetail.refundableUntil', { date: format(cancellationDate, "d MMMM", { locale: dateLocale }) });
  };

  const getCancellationPolicyType = (room: IterpecHotelRoom) => {
    if (!room.CancellationPolicies || room.CancellationPolicies.length === 0) {
      return t('hotelDetail.nonRefundable');
    }
    
    const firstPolicy = room.CancellationPolicies[0];
    const parseDateOnly = (input: string) => {
      const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d));
      }
      const d = new Date(input);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const policyStartDate = parseDateOnly(firstPolicy.StartDate);
    const searchDate = new Date();
    searchDate.setHours(0, 0, 0, 0);
    const searchDatePlus5 = new Date(searchDate);
    searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);

    if (policyStartDate <= searchDatePlus5) {
      return t('hotelDetail.nonRefundable');
    }
    
    return t('hotelDetail.refundable');
  };

  const handleBookRoom = async (room: IterpecHotelRoom) => {
    setIsBooking(true);
    try {
      if (!hotel?.HotelId || !hotel?.Name || !room.Id) {
        throw new Error("Dados do hotel não disponíveis");
      }
      
      const roomPrice = getRoomPriceEur(room);
      const cancellationPolicy = formatCancellationPolicy(room);
      const cancellationType = getCancellationPolicyType(room);
      
      const productPayload = {
        hotelId: hotel.HotelId,
        roomId: room.Id.toString(),
        roomDescription: room.RoomDescription || 'Quarto',
        bedDescription: room.BedDescription || null,
        boardDescription: room.BoardDescription || null,
        priceEur: roomPrice,
        hotelName: hotel.Name,
      };

      const validationResult = hotelProductSchema.safeParse(productPayload);
      
      if (!validationResult.success) {
        const errors = validationResult.error.issues.map(e => `${e.path.join('.')}: ${e.message}`).join(", ");
        throw new Error(`Dados inválidos: ${errors}`);
      }

      const { data: productData, error: productError } = await supabase.functions.invoke(
        'save-hotel-product',
        { body: validationResult.data }
      );

      if (productError || !productData?.success) {
        throw new Error('Failed to save product');
      }

      const { data: exchangeRateResponse } = await supabase.functions.invoke('exchange-rate');
      const exchangeRate = exchangeRateResponse?.data?.rate || 0.1625;
      const cancellationStartDate = room.CancellationPolicies?.[0]?.StartDate || null;

      await addItem({
        productId: productData.productId,
        quantity: 1,
        searchToken: searchId || undefined,
        bookingDetails: {
          type: 'hotel',
          hotelId: hotel.HotelId,
          roomId: room.Id,
          roomName: room.RoomDescription,
          bedDescription: room.BedDescription,
          boardType: room.BoardDescription,
          hotelName: hotel.Name,
          hotelAddress: '',
          checkIn,
          checkOut: format(checkOutDate, 'yyyy-MM-dd'),
          numNights,
          cancellationPolicy,
          cancellationType,
          cancellationStartDate,
          priceEur: roomPrice,
          propertyTaxesEur: room.PropertyTaxesEur,
          exchangeRate,
          adults: parseInt(adults!),
          childrenAges: childrenAges ? childrenAges.split(',').map(Number) : [],
        }
      });
      
      await queryClient.refetchQueries({ queryKey: ["cart-items"] });
      await queryClient.refetchQueries({ queryKey: ["cart"] });
      
      navigate('/checkout', { state: { pendingAdd: true } });
    } catch (error) {
      console.error('Error booking room:', error);
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('errors.generic'),
        variant: "destructive",
      });
    } finally {
      setIsBooking(false);
    }
  };

  const scrollToRooms = () => {
    roomsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sortedRooms = useMemo(() => {
    return hotel?.Rooms ? [...hotel.Rooms].sort((a, b) => getRoomPriceEur(a) - getRoomPriceEur(b)) : [];
  }, [hotel?.Rooms]);

  // Extract unique board types for filter
  const uniqueBoardTypes = useMemo(() => {
    if (!sortedRooms.length) return [];
    const boardTypes = new Set<string>();
    sortedRooms.forEach(room => {
      if (room.BoardDescription) {
        boardTypes.add(room.BoardDescription);
      }
    });
    return Array.from(boardTypes);
  }, [sortedRooms]);

  // Helper to check if a room has breakfast included
  const hasBreakfastIncluded = (boardDescription: string | undefined) => {
    if (!boardDescription) return false;
    const board = boardDescription.toLowerCase();
    const breakfastIncludedKeywords = [
      'breakfast', 'desayuno', 'café da manhã', 'cafe da manha',
      'bed and breakfast', 'half board', 'full board', 'all inclusive',
      'media pensión', 'media pension', 'pensión completa', 'pension completa',
      'meia pensão', 'meia pensao', 'pensão completa', 'pensao completa',
      'todo incluido', 'tudo incluído', 'tudo incluido'
    ];
    const noBreakfastKeywords = [
      'no breakfast', 'sin desayuno', 'sem café', 'sem cafe',
      'room only', 'solo alojamiento', 'apenas acomodação', 'apenas acomodacao',
      'no meals', 'sin comidas', 'sem refeições', 'sem refeicoes'
    ];
    if (noBreakfastKeywords.some(keyword => board.includes(keyword))) {
      return false;
    }
    return breakfastIncludedKeywords.some(keyword => board.includes(keyword));
  };

  // Filtered rooms based on selected filters
  const filteredRooms = useMemo(() => {
    return sortedRooms.filter(room => {
      // Board type filter
      if (boardTypeFilter !== 'all') {
        if (boardTypeFilter === 'with_breakfast') {
          if (!hasBreakfastIncluded(room.BoardDescription)) return false;
        } else if (boardTypeFilter === 'without_breakfast') {
          if (hasBreakfastIncluded(room.BoardDescription)) return false;
        } else {
          if (room.BoardDescription !== boardTypeFilter) return false;
        }
      }
      
      // Cancellation filter
      if (cancellationFilter !== 'all') {
        const cancellationType = getCancellationPolicyType(room);
        const isRefundable = cancellationType === t('hotelDetail.refundable');
        if (cancellationFilter === 'refundable' && !isRefundable) return false;
        if (cancellationFilter === 'non_refundable' && isRefundable) return false;
      }
      
      return true;
    });
  }, [sortedRooms, boardTypeFilter, cancellationFilter, t]);

  const lowestPrice = sortedRooms[0] ? getRoomPricePerNight(sortedRooms[0]) : 0;

  const images = useMemo(() => {
    if (!hotel) return [];
    
    const photoList: Array<{ url: string; title?: string }> = [];
    let photosData: unknown = hotel.Photos as unknown;
    
    if (typeof photosData === 'string') {
      try {
        photosData = JSON.parse(photosData);
      } catch (e) {
        photosData = [];
      }
    }

    if (Array.isArray(photosData) && photosData.length > 0) {
      photosData.forEach((photo: any) => {
        if (typeof photo === 'string') {
          photoList.push({ url: photo });
        } else if (photo && typeof photo === 'object') {
          const url = (photo.url || photo.Url) as string | undefined;
          if (url) {
            const description = photo.description?.['pt-BR'] || photo.description?.es || photo.description?.en || photo.Title || '';
            photoList.push({ url, title: description });
          }
        }
      });
    }

    if (photoList.length === 0 && hotel.ThumbUrl) {
      photoList.push({ url: hotel.ThumbUrl });
    }

    return photoList;
  }, [hotel]);

  const checkInDate = checkIn ? parse(checkIn, 'yyyy-MM-dd', new Date()) : new Date();
  const checkOutDate = addDays(checkInDate, parseInt(numNights || '1'));
  
  const amenityCategories = useMemo(() => {
    if (!hotel) return [];
    return extractAmenitiesFromText(`${hotel.HotelDescription || ''} ${hotel.Description || ''}`);
  }, [hotel]);
  
  const latitude = hotel?.Position?.Latitude || hotel?.Latitude;
  const longitude = hotel?.Position?.Longitude || hotel?.Longitude;
  const hasCoordinates = latitude && longitude;
  
  // Fetch reverse geocode data for Schema.org
  const { data: geocodeData } = useReverseGeocode(latitude, longitude);

  const hotelSchema = useMemo(() => {
    if (!hotel) return null;
    const imageUrls = images.map(img => img.url).filter(Boolean);
    // Merge manual photos with hotel images for richer Schema.org data
    const manualPhotos = manualInfo?.photos || [];
    const allImages = [...manualPhotos, ...imageUrls].filter(Boolean);
    
    const amenityList = amenityCategories.flatMap(cat => cat.amenities.map(item => item.name));
    
    // Get localized review text from manual info
    const reviewText = manualInfo ? (() => {
      const lang = i18n.language;
      if (lang.startsWith('pt')) return manualInfo.review_text_pt;
      if (lang.startsWith('en')) return manualInfo.review_text_en;
      return manualInfo.review_text_es;
    })() : null;
    
    // Get check-in/check-out times from first room if available
    const firstRoom = sortedRooms[0];
    const checkInTime = firstRoom?.CheckInHour || "14:00";
    const checkOutTime = firstRoom?.CheckOutHour || "12:00";
    
    return getHotelDetailSchema({
      name: hotel.Name,
      description: displayDescription,
      url: typeof window !== 'undefined' ? window.location.href : `https://www.foradarota.com/hoteles/${hotel.HotelId}`,

      images: allImages,
      starRating: hotel.Category ? parseInt(hotel.Category) : hotel.StarRating,
      address: hotel.Address,
      city: hotel.City,
      postalCode: geocodeData?.postalCode || undefined,
      addressLocality: geocodeData?.addressLocality || undefined,
      addressRegion: geocodeData?.addressRegion || undefined,
      phone: hotel.Phone?.replace(/['"]/g, ''),
      latitude,
      longitude,
      checkInTime: checkInTime,
      checkOutTime: checkOutTime,
      checkInDate: checkIn || undefined,
      checkOutDate: format(checkOutDate, 'yyyy-MM-dd'),
      rooms: sortedRooms.slice(0, 10).map(room => ({
        name: room.RoomDescription || t('common.room'),
        description: room.BedDescription,
        pricePerNight: getRoomPricePerNight(room),
        totalPrice: getRoomPriceEur(room),
        bedType: room.BedDescription,
        mealPlan: room.BoardDescription,
        occupancy: {
          adults: parseInt(adults || '2'),
          children: childrenAges ? childrenAges.split(',').length : 0
        }
      })),
      amenities: amenityList,
      petsAllowed: manualInfo?.is_pet_friendly ?? undefined,
      review: reviewText ? { text: reviewText, author: "Fora da Rota" } : undefined,
      hotelId: hotel.HotelId
    }, locale as LocaleKey);
  }, [hotel, images, amenityCategories, sortedRooms, displayDescription, latitude, longitude, adults, childrenAges, t, locale, manualInfo, i18n.language, geocodeData]);

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const breadcrumbSchema = useMemo(() => {
    if (!hotel) return null;
    return getBreadcrumbSchema([
      { name: t('nav.home'), url: baseUrl },
      origin === 'resorts'
        ? { name: t('nav.resorts'), url: getLocalizedUrl(locale as LocaleKey, '/resorts') }
        : { name: t('nav.hotels'), url: getLocalizedUrl(locale as LocaleKey, '/hoteles') },
      { name: hotel.Name }
    ], typeof window !== 'undefined' ? window.location.href : `https://www.foradarota.com/hoteles/${hotel.HotelId}`);
  }, [hotel, t, locale, baseUrl, origin]);

  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);

  // Product schema for Google rich snippets with price display
  // Google requires: offers, review, or aggregateRating - we always provide offers + review when available
  const productSchema = useMemo(() => {
    if (!hotel || sortedRooms.length === 0) return null;
    const imageUrls = images.map(img => img.url).filter(Boolean);
    const prices = sortedRooms.map(room => getRoomPriceEur(room));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    // Get localized review text from manual info for Product schema
    const reviewText = manualInfo ? (() => {
      const lang = i18n.language;
      if (lang.startsWith('pt')) return manualInfo.review_text_pt;
      if (lang.startsWith('en')) return manualInfo.review_text_en;
      return manualInfo.review_text_es;
    })() : null;
    
    return getHotelProductSchema({
      name: hotel.Name,
      description: displayDescription?.slice(0, 300),
      url: typeof window !== 'undefined' ? window.location.href : `https://www.foradarota.com/hoteles/${hotel.HotelId}`,
      images: imageUrls,
      starRating: hotel.Category ? parseInt(hotel.Category) : hotel.StarRating,
      address: hotel.Address,
      minPrice, // Required - Google needs offers
      maxPrice,
      offerCount: sortedRooms.length,
      checkIn,
      checkOut: format(checkOutDate, 'yyyy-MM-dd'),
      review: reviewText ? { text: reviewText, author: "Fora da Rota" } : undefined,
    }, locale as LocaleKey);
  }, [hotel, sortedRooms, images, displayDescription, checkIn, checkOutDate, locale, manualInfo, i18n.language]);

  // FAQ schema for SEO - pass URL for @id linking
  const faqSchema = useMemo(() => {
    if (hotelFaqItems.length === 0) return null;
    return getFAQSchema(hotelFaqItems, typeof window !== 'undefined' ? window.location.href : 'https://www.foradarota.com');
  }, [hotelFaqItems]);

  const propertyHighlights = useMemo(() => {
    const highlights = [];
    if (amenityCategories.some(cat => cat.amenities.some(a => a.name.toLowerCase().includes('wifi') || a.name.toLowerCase().includes('internet')))) {
      highlights.push({ icon: Wifi, label: 'WiFi' });
    }
    if (amenityCategories.some(cat => cat.amenities.some(a => a.name.toLowerCase().includes('parking') || a.name.toLowerCase().includes('estacionamento') || a.name.toLowerCase().includes('aparcamiento')))) {
      highlights.push({ icon: Car, label: t('hotelDetail.parking') || 'Parking' });
    }
    if (amenityCategories.some(cat => cat.amenities.some(a => a.name.toLowerCase().includes('breakfast') || a.name.toLowerCase().includes('café') || a.name.toLowerCase().includes('desayuno')))) {
      highlights.push({ icon: Coffee, label: t('hotelDetail.breakfast') || 'Breakfast' });
    }
    if (amenityCategories.some(cat => cat.amenities.some(a => a.name.toLowerCase().includes('pool') || a.name.toLowerCase().includes('piscina')))) {
      highlights.push({ icon: Waves, label: t('hotelDetail.pool') || 'Pool' });
    }
    return highlights;
  }, [amenityCategories, t]);

  if (isLoading) {
    return <HotelDetailLoading />;
  }

  if (!hotel) {
    return null;
  }

  return (
    <>
      <HreflangTags path={`/hoteles/${hotelId}`} />
{hotelSchema && breadcrumbSchema && productSchema && faqSchema
        ? <SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, hotelSchema, productSchema, faqSchema]} />
        : hotelSchema && breadcrumbSchema && productSchema
          ? <SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, hotelSchema, productSchema]} />
          : hotelSchema && breadcrumbSchema
            ? <SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, hotelSchema]} />
            : <SchemaOrg schema={[organizationSchema, webSiteSchema]} />
      }
      <Navbar />
      
      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <div className="border-b bg-card animate-fade-in">
          <div className="container mx-auto px-4 py-6">
            {/* Back button - moved to top */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // If coming from resorts, go back to resorts page
                if (origin === 'resorts' && resortDestinationId) {
                  const params = new URLSearchParams();
                  params.append('resortDestinationId', resortDestinationId);
                  if (destinationName) params.append('resortDestinationName', destinationName);
                  if (checkIn) params.append('checkIn', checkIn);
                  if (numNights) {
                    const checkInDate = new Date(checkIn || '');
                    const checkOutDate = new Date(checkInDate);
                    checkOutDate.setDate(checkOutDate.getDate() + parseInt(numNights));
                    params.append('checkOut', checkOutDate.toISOString().split('T')[0]);
                  }
                  if (adults) params.append('adults', adults);
                  if (childrenAges) params.append('childrenAges', childrenAges);
                  navigate(`/resorts?${params.toString()}`);
                } else {
                  // Default: go back to hotels page
                  const params = new URLSearchParams();
                  if (destinationId) params.append('destinationId', destinationId);
                  if (checkIn) params.append('checkIn', checkIn);
                  if (numNights) params.append('numNights', numNights);
                  if (adults) params.append('adults', adults);
                  if (childrenAges) params.append('childrenAges', childrenAges);
                  navigate(`/hoteles?${params.toString()}`);
                }
              }}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {origin === 'resorts' ? t('hotelDetail.backToResorts') : t('hotelDetail.backToResults')}
            </Button>

            <Breadcrumb
              items={[
                { label: t('nav.home'), href: "/" },
                origin === 'resorts' 
                  ? { label: t('nav.resorts'), href: "/resorts" }
                  : { label: t('nav.hotels'), href: "/hoteles" },
                { label: hotel.Name },
              ]}
            />

            <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                {/* Stars */}
                {hotel.Category && (
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: parseInt(hotel.Category) || 0 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                )}
                
                {/* Hotel Name */}
                <h1 ref={hotelNameRef} className="text-3xl lg:text-4xl font-bold tracking-tight mb-3">
                  {hotel.Name}
                </h1>
                
                {/* Address with map link */}
                {hotel.Address && (
                  <button 
                    onClick={() => hasCoordinates && setIsMapOpen(true)}
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="group-hover:underline">{hotel.Address}</span>
                    {hasCoordinates && (
                      <span className="text-sm text-primary font-medium">- {t('hotels.viewOnMap')}</span>
                    )}
                  </button>
                )}
              </div>

              {/* Price & CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full lg:w-auto lg:text-right">
                {/* Admin Edit Button */}
                {isAdmin && (
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsManualInfoEditorOpen(true)}
                      className="gap-2 flex-1 sm:flex-none"
                    >
                      <Pencil className="h-4 w-4" />
                      {manualInfo ? "Editar Info" : "Agregar Info"}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsFaqEditorOpen(true)}
                      className="gap-2 flex-1 sm:flex-none"
                    >
                      <HelpCircle className="h-4 w-4" />
                      FAQ
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="flex-shrink-0">
                    <p className="text-sm text-muted-foreground">{t('common.from')}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-primary">{formatPrice(lowestPrice)}</p>
                    <p className="text-sm text-muted-foreground">/{t('common.night')}</p>
                  </div>
                  <Button size="lg" onClick={scrollToRooms} className="whitespace-nowrap flex-1 sm:flex-none min-w-0">
                    {t('hotels.selectRoom')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">

          {/* Image Gallery */}
          {images.length > 0 && (
            <div className="mb-8 animate-fade-in relative" style={{ animationDelay: '0.1s', animationFillMode: 'backwards' }}>
              {/* Recommended Hotel Badge */}
              {manualInfo && (
                <div className="absolute top-4 left-4 z-10">
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg px-4 py-2 text-sm font-semibold gap-2">
                    <Star className="h-4 w-4 fill-white" />
                    {t('hotelDetail.recommendedHotel')}
                  </Badge>
                </div>
              )}
              <EnhancedImageGallery images={images} productName={hotel.Name} />
            </div>
          )}

          {/* Main Content with Sidebar */}
          <div className="grid lg:grid-cols-3 gap-8 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'backwards' }}>
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Property Highlights */}
              {propertyHighlights.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {propertyHighlights.map((highlight, index) => (
                    <div key={index} className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                      <highlight.icon className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium">{highlight.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Overview / Description */}
              {hotel.HotelDescription && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t('hotelDetail.aboutHotel')}</h2>
                    {isTranslating ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        <span>{t('hotelDetail.translating')}</span>
                      </div>
                    ) : (
                      <>
                        <div className={`overflow-hidden transition-all duration-300 ${
                          isDescriptionExpanded ? 'max-h-none' : 'max-h-32'
                        }`}>
                          <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                            {displayDescription?.split('\n').map((line, index) => {
                              // Check if line is a subtitle (wrapped in **)
                              const subtitleMatch = line.match(/^\*\*(.+)\*\*$/);
                              if (subtitleMatch) {
                                const subtitleText = subtitleMatch[1].toLowerCase();
                                // Map keywords to icons
                                const getSubtitleIcon = () => {
                                  if (subtitleText.includes('ubicación') || subtitleText.includes('localização') || subtitleText.includes('location')) return MapPin;
                                  if (subtitleText.includes('habitacion') || subtitleText.includes('quarto') || subtitleText.includes('room')) return Bed;
                                  if (subtitleText.includes('restaurante') || subtitleText.includes('gastronom') || subtitleText.includes('cocina') || subtitleText.includes('comida') || subtitleText.includes('dining')) return UtensilsCrossed;
                                  if (subtitleText.includes('piscina') || subtitleText.includes('pool') || subtitleText.includes('agua')) return Waves;
                                  if (subtitleText.includes('spa') || subtitleText.includes('bienestar') || subtitleText.includes('wellness') || subtitleText.includes('relax')) return Sparkles;
                                  if (subtitleText.includes('gimnasio') || subtitleText.includes('fitness') || subtitleText.includes('deporte') || subtitleText.includes('sport')) return Dumbbell;
                                  if (subtitleText.includes('playa') || subtitleText.includes('praia') || subtitleText.includes('beach')) return Umbrella;
                                  if (subtitleText.includes('jardín') || subtitleText.includes('jardim') || subtitleText.includes('garden') || subtitleText.includes('naturaleza')) return TreePine;
                                  if (subtitleText.includes('montaña') || subtitleText.includes('vista') || subtitleText.includes('mountain') || subtitleText.includes('view')) return Mountain;
                                  if (subtitleText.includes('servicio') || subtitleText.includes('serviço') || subtitleText.includes('service') || subtitleText.includes('comodidad')) return Heart;
                                  if (subtitleText.includes('seguridad') || subtitleText.includes('segurança') || subtitleText.includes('security')) return Shield;
                                  if (subtitleText.includes('niño') || subtitleText.includes('criança') || subtitleText.includes('child') || subtitleText.includes('familia')) return Baby;
                                  if (subtitleText.includes('accesib') || subtitleText.includes('movilidad')) return Accessibility;
                                  if (subtitleText.includes('parking') || subtitleText.includes('estacion') || subtitleText.includes('aparcamiento')) return ParkingCircle;
                                  if (subtitleText.includes('wifi') || subtitleText.includes('internet') || subtitleText.includes('conectividad')) return Wifi;
                                  if (subtitleText.includes('desayuno') || subtitleText.includes('café') || subtitleText.includes('breakfast')) return Coffee;
                                  if (subtitleText.includes('transporte') || subtitleText.includes('traslado') || subtitleText.includes('aeropuerto')) return Plane;
                                  if (subtitleText.includes('horario') || subtitleText.includes('check') || subtitleText.includes('hora')) return Clock;
                                  if (subtitleText.includes('edificio') || subtitleText.includes('hotel') || subtitleText.includes('instalacion')) return Building2;
                                  if (subtitleText.includes('atracci') || subtitleText.includes('turismo') || subtitleText.includes('cultural')) return Landmark;
                                  return Info;
                                };
                                const IconComponent = getSubtitleIcon();
                                return (
                                  <h3 key={index} className="flex items-center gap-2 font-semibold text-foreground mt-4 mb-2 first:mt-0">
                                    <IconComponent className="h-4 w-4 text-primary shrink-0" />
                                    {subtitleMatch[1]}
                                  </h3>
                                );
                              }
                              // Regular line
                              return line ? <p key={index} className="mb-2 last:mb-0">{line}</p> : <br key={index} />;
                            })}
                          </div>
                        </div>
                        {displayDescription && displayDescription.length > 250 && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                            className="mt-2 px-0 h-auto"
                          >
                            {isDescriptionExpanded ? (
                              <>
                                {t('hotelDetail.showLess')}
                                <ChevronUp className="h-4 w-4 ml-1" />
                              </>
                            ) : (
                              <>
                                {t('hotelDetail.showMore')}
                                <ChevronDown className="h-4 w-4 ml-1" />
                              </>
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Manual Info Section - Our Review */}
              {manualInfo && (
                <HotelManualInfoSection manualInfo={manualInfo} />
              )}

              {/* Amenities */}
              {amenityCategories.length > 0 && (
                <HotelAmenities categories={amenityCategories} />
              )}

              {/* Location Map */}
              {hasCoordinates && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t('hotelDetail.location')}</h2>
                    <HotelStaticMap
                      latitude={latitude!}
                      longitude={longitude!}
                      hotelName={hotel.Name}
                      onClick={() => setIsMapOpen(true)}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Booking Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="shadow-lg border-2">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <p className="text-sm text-muted-foreground mb-1">{t('common.from')}</p>
                      <p className="text-4xl font-bold text-primary">{formatPrice(lowestPrice)}</p>
                      <p className="text-sm text-muted-foreground">/{t('common.night')}</p>
                    </div>

                    <Button 
                      size="lg" 
                      className="w-full mb-6" 
                      onClick={scrollToRooms}
                    >
                      {t('hotels.selectRoom')}
                    </Button>

                    {/* Search Info */}
                    <div className="p-4 bg-muted rounded-lg mb-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">
                              {format(checkInDate, "d MMM", { locale: dateLocale })} - {format(checkOutDate, "d MMM yyyy", { locale: dateLocale })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {numNights} {parseInt(numNights || '1') === 1 ? t('hotelDetail.night') : t('hotelDetail.nights')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">
                              {t('hotelDetail.adults', { count: parseInt(adults || '2') })}
                              {childrenAges && childrenAges.length > 0 ? `, ${t('hotelDetail.children', { count: childrenAges.split(',').length })}` : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button 
                      variant="outline"
                      size="lg" 
                      className="w-full" 
                      onClick={() => setIsSearchEditModalOpen(true)}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('searchEdit.modify')}
                    </Button>

                    {/* Quick Benefits */}
                    <div className="mt-6 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{t('hotelDetail.bestPriceGuaranteed')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Available Rooms Section */}
          <div ref={roomsSectionRef} className="mt-12 scroll-mt-24">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-2xl font-bold">{t('hotelDetail.availableRooms')}</h2>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Board Type Filter */}
                <select
                  value={boardTypeFilter}
                  onChange={(e) => {
                    setBoardTypeFilter(e.target.value);
                    setVisibleRoomsCount(5);
                  }}
                  className="h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">{t('room.allBoardTypes')}</option>
                  <option value="with_breakfast">{t('room.withBreakfast')}</option>
                  <option value="without_breakfast">{t('room.withoutBreakfast')}</option>
                </select>

                {/* Cancellation Filter */}
                <select
                  value={cancellationFilter}
                  onChange={(e) => {
                    setCancellationFilter(e.target.value);
                    setVisibleRoomsCount(5);
                  }}
                  className="h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">{t('room.allCancellation')}</option>
                  <option value="refundable">{t('room.onlyRefundable')}</option>
                  <option value="non_refundable">{t('room.onlyNonRefundable')}</option>
                </select>

                {/* Clear Filters */}
                {(boardTypeFilter !== 'all' || cancellationFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setBoardTypeFilter('all');
                      setCancellationFilter('all');
                      setVisibleRoomsCount(5);
                    }}
                    className="h-9 px-2"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('common.clearFilters')}
                  </Button>
                )}
              </div>
            </div>

            {/* Filter summary */}
            {filteredRooms.length !== sortedRooms.length && (
              <p className="text-sm text-muted-foreground mb-4">
                {t('room.showingFiltered', { filtered: filteredRooms.length, total: sortedRooms.length })}
              </p>
            )}
            
            <div className="space-y-4">
              {filteredRooms.length > 0 ? (
                <>
                  {filteredRooms.slice(0, visibleRoomsCount).map((room, index) => {
                    const roomPrice = getRoomPriceEur(room);
                    const pricePerNight = getRoomPricePerNight(room);
                    const cancellationPolicy = formatCancellationPolicy(room);
                    const cancellationType = getCancellationPolicyType(room);
                    
                    const checkInDateParsed = parse(checkIn!, 'yyyy-MM-dd', new Date());
                    const checkOutDateCalculated = addDays(checkInDateParsed, parseInt(numNights || '1'));
                    const checkOut = format(checkOutDateCalculated, 'yyyy-MM-dd');

                    return (
                      <div key={index} className="animate-fade-in">
                        <RoomCard 
                          room={room}
                          roomPrice={roomPrice}
                          pricePerNight={pricePerNight}
                          cancellationPolicy={cancellationPolicy}
                          cancellationType={cancellationType}
                          numNights={parseInt(numNights || '1')}
                          onBookRoom={handleBookRoom}
                          hotelId={parseInt(hotelId!)}
                          hotelName={hotel.Name}
                          checkIn={checkIn!}
                          checkOut={checkOut}
                          searchId={searchId}
                          isBooking={isBooking}
                        />
                      </div>
                    );
                  })}
                  
                  {filteredRooms.length > visibleRoomsCount && (
                    <div className="flex justify-center mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setVisibleRoomsCount(prev => prev + 5)}
                        className="min-w-[200px]"
                      >
                        {t('hotelDetail.moreRooms', { count: filteredRooms.length - visibleRoomsCount })}
                      </Button>
                    </div>
                  )}
                  
                  {visibleRoomsCount >= filteredRooms.length && filteredRooms.length > 5 && (
                    <div className="flex justify-center mt-6">
                      <Button
                        variant="ghost"
                        onClick={() => setVisibleRoomsCount(5)}
                        className="min-w-[200px]"
                      >
                        {t('hotelDetail.lessRooms')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{t('room.noRoomsMatchFilter')}</p>
                  <Button
                    variant="link"
                    onClick={() => {
                      setBoardTypeFilter('all');
                      setCancellationFilter('all');
                    }}
                    className="mt-2"
                  >
                    {t('common.clearFilters')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Hotel FAQ Section - After Rooms */}
          <div className="container mx-auto px-4 pb-12">
            <HotelFAQ 
              hotel={hotel}
              rooms={sortedRooms}
              manualInfo={manualInfo}
              amenityCategories={amenityCategories}
              onFaqItemsChange={setHotelFaqItems}
            />
          </div>
        </div>
      </div>

      {/* Map Modal - Lazy loaded */}
      {hasCoordinates && (
        <LazyInteractiveHotelMap
          isOpen={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          latitude={latitude!}
          longitude={longitude!}
          hotelName={hotel.Name}
        />
      )}

      {/* Search Edit Modal */}
      <SearchEditModal
        open={isSearchEditModalOpen}
        onOpenChange={setIsSearchEditModalOpen}
        currentCheckIn={checkIn || ''}
        currentNumNights={numNights || '7'}
        currentAdults={adults || '2'}
        currentChildrenAges={childrenAges || ''}
        onSearch={handleSearchWithNewParams}
        isSearching={isSearching}
      />

      {/* No Availability Modal */}
      <NoAvailabilityModal
        open={isNoAvailabilityModalOpen}
        onOpenChange={setIsNoAvailabilityModalOpen}
        onStayOnPage={handleStayOnPage}
        onGoToResults={handleGoToResults}
      />

      {/* Manual Info Editor (Admin only) */}
      {hotel && (
        <HotelManualInfoEditor
          hotelId={hotel.HotelId}
          hotelName={hotel.Name}
          existingInfo={manualInfo}
          open={isManualInfoEditorOpen}
          onOpenChange={setIsManualInfoEditorOpen}
          onSaved={refetchManualInfo}
        />
      )}

      {/* FAQ Editor (Admin only) */}
      {hotel && (
        <HotelFAQEditor
          hotelId={hotel.HotelId}
          hotelName={hotel.Name}
          hotel={hotel}
          rooms={sortedRooms}
          manualInfo={manualInfo}
          amenityCategories={amenityCategories}
          open={isFaqEditorOpen}
          onOpenChange={setIsFaqEditorOpen}
          onSaved={refetchManualFaq}
        />
      )}

      <Footer />
    </>
  );
};

export default HotelDetail;
